import * as React from "react";

import { Box } from "@mui/material";

import DataGridTableInternal from "./DataGridTableInternal";
import ComponentLoader from "../misc/ComponentLoader";

import { isEmpty } from "../../helper-functions/shared/miscUtils.js";

import * as Constants from "../../config/constants/Constants.js";

/**
 * A universal wrapper for the Table component.
 * Includes:
 * - query callback when chaning page, filter, or sort
 * - a loader (spinner)
 * - empty text in case of no rows
 *
 * Required:
 * - states (e.g. props.signals.external.owner)
 * - columns (defines the table columns)
 * - query (callback function to query data with 'options' as parameter)
 *
 * Optional:
 * empty (component to show when no entries)
 * refresh (query data with current filters and sort every 'refresh' seconds)
 *
 * See other props via the Table component.
 */
export default function DataGridTable(props) {
    const { states, columns, query } = props;
    const { empty, refresh, pagination } = props;

    const { data: rows } = states;
    const { page, setPage, pageSize, setPageSize } = states;
    const { filter, setFilter, sort, setSort, selected, setSelected, visibility, setVisibility } = states;
    const { queriedUntil, rowCount, queryLoading, setQueryLoading } = states;

    const options = { page, pageSize, filter, sort };
    const currentOptions = React.useRef(options);

    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        currentOptions.current = options;
    }, [{ ...options }]);

    // After a timeout, show the "no entries" text which replaces the table.
    React.useEffect(() => {
        const loop = setTimeout(() => setLoading(false), Constants.TABLE_LOADING_MS);

        // Return a function to clear the interval when needed.
        return () => clearTimeout(loop);
    }, []);

    // Hide loader when data is loaded.
    React.useEffect(() => {
        if (!isEmpty(rows)) setLoading(false);
    }, [rows]);

    // Always query when the component mounts.
    React.useEffect(() => {
        const timeout = isEmpty(rows) ? 0 : 300;

        const loop = setTimeout(() => {
            query(currentOptions.current);
        }, timeout);

        return () => clearTimeout(loop);
    }, []);

    // Query data when page changes.
    React.useEffect(() => {
        if (page === 0 || page <= queriedUntil) return;

        setQueryLoading(true);
        setTimeout(() => setQueryLoading(false), 1000);

        query(options);
    }, [page]);

    // Query data when filters change, and go to first page.
    React.useEffect(() => {
        if (loading) return;

        setPage(0);
        query({ ...options, page: 0 });
    }, [pageSize, filter, sort]);

    // Periodically query data with current options.
    React.useEffect(() => {
        if (!refresh) return;

        const loop = setInterval(() => {
            query(currentOptions.current);
        }, refresh * 1000);

        // Clear the interval when the component unmounts.
        return () => clearInterval(loop);
    }, []);

    return (
        <Box>
            {!loading && rows.length !== 0 && (
                <DataGridTableInternal
                    {...props}
                    // Table data.
                    rows={rows}
                    columns={columns}
                    // Pagination.
                    pagination={pagination}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    rowCount={rowCount}
                    queryLoading={queryLoading}
                    // Filter, sort, selection, column visibility.
                    filter={filter}
                    setFilter={setFilter}
                    sort={sort}
                    setSort={setSort}
                    selected={selected}
                    setSelected={setSelected}
                    visibility={visibility}
                    setVisibility={setVisibility}
                />
            )}

            {/* Show text if empty */}
            <ComponentLoader isVisible={loading || rows.length === 0} isEmpty={!loading && rows.length === 0} emptyText={empty} height={"300px"} />
        </Box>
    );
}
